// @flow
import React, { type Node, type Element } from 'react';
import FixedHeader from 'app/common/react/components/profile/UserHeader/FixedHeader';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import UserInfo from 'app/common/react/components/profile/UserHeader/UserInfo';
import { compose } from 'redux';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import { Avatar, Box, Column, Container, Heading, Text, Touchable } from 'gestalt';

type Props = {|
  count?: Node,
  isProfile?: boolean,
  label?: string,
  onCreatePincodeClick?: () => void,
  onToggleBlock: () => void,
  onToggleFollow: () => void,
  showFixedHeaderName: ?boolean,
  user: ProfileUser,
  viewer: Viewer,
|};

const isOwnProfile = ({ user, viewer }: { user: ProfileUser, viewer: Viewer }) =>
  user && viewer && viewer.isAuth && user.username === viewer.username;

const CondensedAvatar = ({ user }: { user: ProfileUser }) => {
  const showVerifiedIcon = user && user.verified_identity && user.verified_identity.verified;
  return (
    <Box height={106} width={106}>
      <Avatar src={user.image_xlarge_url} name={user.username} verified={showVerifiedIcon} />
    </Box>
  );
};

type InteractableProps = {|
  children: Element<typeof CondensedAvatar>,
  onTouch: ?() => void,
  username: string,
|};

export const Interactable = ({ children, onTouch, username }: InteractableProps) =>
  onTouch ? (
    <Touchable onTouch={onTouch} shape="rounded" fullWidth={false}>
      {children}
    </Touchable>
  ) : (
    <NavigateLink to={`/${username}/`}>{children}</NavigateLink>
  );

export class UserHeader extends React.Component<Props> {
  container: ?HTMLElement;

  render() {
    const {
      count,
      isProfile,
      label,
      onCreatePincodeClick,
      onToggleBlock,
      onToggleFollow,
      showFixedHeaderName,
      user,
      user: { username },
      viewer,
    } = this.props;
    const ownProfile = isOwnProfile({ user, viewer });
    const profilePincodeIsEnabled = ownProfile || (viewer.isAuth && viewer.isEmployee);

    return (
      <div
        ref={(ref) => {
          this.container = ref;
        }}
      >
        {isProfile && (
          <FixedHeader
            isOwnProfile={ownProfile}
            onToggleBlock={onToggleBlock}
            onToggleFollow={onToggleFollow}
            showFixedHeaderName={showFixedHeaderName}
            user={user}
          />
        )}
        <Container>
          <Box
            alignItems="center"
            display="flex"
            direction="row"
            justifyContent="between"
            marginTop={10}
            width="100%"
          >
            <Column span={9}>
              {!isProfile ? (
                <Box display="flex" direction="column">
                  <Heading size="sm">{count}</Heading>
                  <Text bold size="sm" color="darkGray">
                    {label}
                  </Text>
                </Box>
              ) : (
                <UserInfo isOwnProfile={ownProfile} user={user} />
              )}
            </Column>
            <Column span={3}>
              <Box marginTop={6} justifyContent="end" display="flex">
                <Interactable
                  onTouch={isProfile && profilePincodeIsEnabled ? onCreatePincodeClick : null}
                  username={username}
                >
                  <CondensedAvatar user={user} />
                </Interactable>
              </Box>
            </Column>
          </Box>
        </Container>
      </div>
    );
  }
}

export default compose(withViewer)(UserHeader);
